import { CheckIcon, CircleDotIcon, DashboardIcon, GiftIcon, KioskIcon } from "@xola/icons";
import clsx from "clsx";
import { startCase } from "lodash";
import React, { FC, ReactNode } from "react";
import { Plugin, Source } from "types";
import { hasMessagingAbility, useAutoFetchPlugins, useInstalledPlugins } from "../../hooks/usePlugins";
import { isBookingEngine } from "../../models/PurchaseItem";
import { NoTranslate } from "./NoTranslate";
import { PluginImage } from "./PluginImage";

// This is from UI Kit
export const iconSizes = {
    tiny: "w-3 h-3", // 12 px
    small: "w-3.5 h-3.5", // 14px
    medium: "w-4.5 h-4.5", // 18px
    large: "w-6 h-6", // 24px
};

interface SourceIconProps {
    source: Source | string;
    size?: keyof typeof iconSizes;
    className?: string;
    defaultIcon?: FC;
}

export const SourceIcon = ({ source, size = "medium", className, defaultIcon }: SourceIconProps) => {
    const Icon = sourceIconMap[source] ?? defaultIcon ?? CircleDotIcon;

    const plugin = useSourcePlugin(source);
    if (plugin) {
        return <PluginImage plugin={plugin} className={clsx(`${iconSizes[size]}`, className)} />;
    }

    return <Icon size={size} className={className} />;
};

export const SourceName = ({ source }: { source: string }) => {
    const plugin = useSourcePlugin(source);
    if (plugin) {
        return <NoTranslate>{plugin.name}</NoTranslate>;
    }

    return <NoTranslate>{startCase(source)}</NoTranslate>;
};

export const useSourcePlugin = (source: string) => {
    const { data: plugins } = useAutoFetchPlugins({ status: "approved" });

    return getSourcePlugin(plugins, source);
};

export const useSourceMessagingPlugin = (source?: string): Plugin | null => {
    const { data: installedPlugins = [] } = useInstalledPlugins();
    const plugin = getSourcePlugin(installedPlugins, source ?? "");
    return plugin && hasMessagingAbility(plugin) ? plugin : null;
};

export const getSourcePlugin = (plugins: any[], source: string) => {
    const mongodbObjectIdRegexPattern = /^[\da-f]{24}$/i;
    const isPluginId = mongodbObjectIdRegexPattern.test(source);

    if (isPluginId && plugins?.length) {
        return plugins.find((p) => p.id === source);
    }

    // In case of other booking engines which has Engine name in source
    if (plugins?.length && isBookingEngine(source)) {
        return plugins.find((p) => p.name.replaceAll(/\s/g, "").toLowerCase().includes(source));
    }

    return null;
};

const sourceIconMap: Record<string, ReactNode> = {
    [Source.OFFICE]: DashboardIcon,
    [Source.CHECKOUT]: CheckIcon,
    [Source.KIOSK]: KioskIcon,
    [Source.GIFT]: GiftIcon,
};
